// @flow
import { has } from "../../utils/helpers/objects";

import ProcessStatusSettingsModel from "../process/ProcessStatusSettingsModel";
import BaseModel from "../base/BaseModel";
import Href from "../href/Href";
import LinkCollection from "../links/LinkCollection";
import { INITIAL_FILTER } from "../../constants/LayoutHints";
import Parameter from "../parameter/Parameter";
import { HTTP_METHODS } from "../../constants";

import type { ModelOptions, ModularUIModel } from "../types";

/**
 * Defines a Link. For instance below example of a link to the tab 'books'
 * <br/>
 * "Books": {
 * "href": "/books",
 * "profile": "/profiles/tab"
 * }
 */
class LinkModel extends BaseModel {
  _href: Href;
  _isCacheable: boolean;
  _icon: string;
  _targetModel: ?Class<ModularUIModel>;
  _method: $Keys<typeof HTTP_METHODS>;

  /**
   * Create a Link
   */
  constructor(
    data: Object,
    contributions: Object,
    modelOptions?: ModelOptions,
  ) {
    super(data, contributions, modelOptions);

    this.createHref(modelOptions);

    this._method = HTTP_METHODS.GET;
    this._isCacheable = false;
  }

  /**
   * Create a simple Link Model
   */
  static create(
    name: string,
    href: string | Href,
    label: string,
    modelOptions?: ModelOptions,
  ): LinkModel {
    const data = { name, href };
    const contributions = { label };
    return new LinkModel(data, contributions, modelOptions);
  }

  /**
   */
  createHref(modelOptions: ?ModelOptions) {
    const href = new Href(this.data.href, this.resourcetype, modelOptions);
    this.handleInitialFilters(href);

    this.href = href;
  }

  /**
   * Handle initial filter hints
   * @param href
   */
  handleInitialFilters(href: Href) {
    if (this.layouthint.has(INITIAL_FILTER)) {
      const filters: { [string]: Array<string> } = {};
      this.layouthint
        .filter((hint) => hint.startsWith(INITIAL_FILTER))
        .forEach((hint) => {
          const param = hint.substr(INITIAL_FILTER.length);
          const p = Parameter.fromString(param);

          if (p != null) {
            if (!filters[p.name]) {
              filters[p.name] = [];
            }

            if (p.value != null) {
              filters[p.name].push(p.value);
            }
          }
        });

      Object.keys(filters).forEach((key) => {
        const value = filters[key].join(",");
        href.addParameter(key, value);
      });
    }
  }

  /**
   * Getting link group
   */
  get group(): string {
    return this.getContribution("group", this.data.group);
  }

  /**
   * Getting the key/name of this link
   */
  get key(): string {
    return this.data.name;
  }

  /**
   * Retrieve the href of the link
   */
  get href(): Href {
    return this._href;
  }

  /**
   * Set the href of the Link
   */
  set href(href: Href) {
    this._href = href;
  }

  /**
   * Indicate how this link must be called, GET by default
   */
  get method(): $Keys<typeof HTTP_METHODS> {
    return this._method;
  }

  /**
   * Set http method for this link
   */
  set method(method: $Keys<typeof HTTP_METHODS>) {
    this._method = method;
  }

  /**
   * Getting the label of the link
   */
  get label(): string {
    return this.getContribution("label", this.key);
  }

  /**
   * Getting the type of the link
   */
  get resourcetype(): string {
    return this.getContribution("resourcetype", "");
  }

  /**
   */
  get links(): LinkCollection {
    if (this.data.components) {
      return new LinkCollection(
        { components: this.data.components },
        { components: this.contributions.components },
      );
    }

    return new LinkCollection();
  }

  /**
   * Check if the href startswith the URI of the given href
   */
  isActive(link: ?LinkModel): boolean {
    if (link) {
      return link.href.startsWith(this.href);
    }

    return false;
  }

  /**
   * Setter for icon of LinkModel
   */
  set icon(icon: string) {
    this._icon = icon;
  }

  /**
   * Retrieve icon of LinkModel
   */
  get icon(): string {
    return this._icon;
  }

  /**
   */
  get targetModel(): ?Class<ModularUIModel> {
    return this._targetModel;
  }

  /**
   */
  set targetModel(targetModel: Class<ModularUIModel>) {
    this._targetModel = targetModel;
  }

  /**
   */
  get isCacheable(): boolean {
    return this._isCacheable;
  }

  /**
   */
  set isCacheable(isCacheable: boolean) {
    this._isCacheable = isCacheable;
  }

  /**
   */
  get filterName(): string {
    return this.data.filter.name;
  }

  /**
   */
  get hasProcessStatusSettings(): boolean {
    return (
      has(this.data, "processStatus") &&
      has(this.contributions, "processStatus")
    );
  }

  /**
   */
  get processStatus(): ProcessStatusSettingsModel | null {
    if (this.hasProcessStatusSettings) {
      return new ProcessStatusSettingsModel(
        this.getData("processStatus", {}),
        this.getContribution("processStatus", {}),
      );
    }

    return null;
  }
}

export default LinkModel;
